<p-toast position="top-right"></p-toast>
<!-- El PDF de la foto se abre aquí; «Descargar» dentro del visor lo guarda con el nombre que manda el backend. -->
<app-file-preview-dialog
  [visible]="!!pdfs.preview"
  [fileBlob]="pdfs.preview?.blob ?? null"
  [fileName]="pdfs.preview?.fileName ?? null"
  [downloadable]="true"
  (visibleChange)="$event || pdfs.closePreview()"
  (downloadRequested)="pdfs.savePreview()"
></app-file-preview-dialog>

<!-- Una fuente sin datos en la línea «Sin registros:», con su estado; el texto largo va al `title` y al lector. -->
<ng-template #sourceChip let-source>
  <span class="alg-src" [attr.data-test]="source.key" [attr.data-state]="source.state" [attr.title]="source.text">
    <i
      aria-hidden="true"
      [ngClass]="
        source.state === 'empty'
          ? 'fa-solid fa-check is-ok'
          : source.state === 'pending'
            ? 'fa-regular fa-clock is-warn'
            : source.state === 'source-error'
              ? 'fa-solid fa-triangle-exclamation is-bad'
              : 'fa-solid fa-minus is-muted'
      "
    ></i
    >{{ source.label }}<span class="p-hidden-accessible">: {{ source.text }}</span>
  </span>
</ng-template>

<section class="snapshot-tab" aria-label="Legal y corporativo">
  <app-snapshot-state *ngIf="envelopeState; else withSnapshot" [state]="envelopeState" (retry)="retry()"></app-snapshot-state>

  <ng-template #withSnapshot>
    <article class="alg-card" *ngIf="view?.snapshot as snapshot">
      <!-- El subtítulo evita que se confunda con la pestaña «Dictamen Legal» del expediente (F-158). -->
      <header class="alg-card__header">
        <span class="alg-card__icon"><i class="fa-solid fa-scale-balanced" aria-hidden="true"></i></span>
        <div class="alg-card__titles">
          <h2>Antecedentes legales y corporativos</h2>
          <p data-test="legal-subtitle">
            Verificación de antecedentes, BIL y Registro Público de Comercio · <strong>no sustituye el Dictamen Legal</strong>
          </p>
        </div>
        <span class="alg-pill is-neutral" *ngIf="snapshot.viewedRevision" data-test="snapshot-revision"
          >Foto · revisión {{ snapshot.viewedRevision }}</span
        >
      </header>

      <app-snapshot-state
        *ngIf="snapshot.reconstructed"
        state="reconstructed"
        [builtAt]="snapshot.reconstructedAt"
        [analyzedAt]="snapshot.originalVerifiedAt ?? analyzedAt"
      ></app-snapshot-state>
      <app-snapshot-state *ngIf="snapshot.pinned" state="pinned"></app-snapshot-state>

      <div class="alg-card__body legal-body">
        <!-- ─── Franja de 4: antecedentes, expedientes, BIL y accionistas ─── -->
        <div class="alg-kpis legal-kpis">
          <div class="alg-kpi" data-test="background-score">
            <span class="alg-kpi__label">Antecedentes · calificación de Syntage</span>
            <span class="alg-kpi__value" title="Syntage califica los antecedentes de 0 a 10; 10 es sin riesgo"
              >{{ backgroundScoreValue ?? '—'
              }}<span class="alg-kpi__unit" *ngIf="backgroundScoreValue !== null"> / {{ backgroundScale }}</span></span
            >
            <span class="alg-kpi__sub" *ngIf="!sectionState(sections.background); else backgroundShort"
              >{{ backgroundRows.length }} {{ backgroundRows.length === 1 ? 'categoría' : 'categorías' }} ·
              <ng-container *ngIf="backgroundAtRisk; else noneAtRisk"
                ><strong class="is-bad">{{ backgroundAtRisk }} en riesgo</strong></ng-container
              ><ng-template #noneAtRisk>ninguna en riesgo</ng-template></span
            >
            <ng-template #backgroundShort
              ><span class="alg-kpi__sub">{{ stateShort[sectionState(sections.background)!] }}</span></ng-template
            >
          </div>
          <div class="alg-kpi" data-test="lawsuits-kpi">
            <span class="alg-kpi__label">Expedientes judiciales</span>
            <span class="alg-kpi__value">{{
              sectionState(sections.lawsuits) && sectionState(sections.lawsuits) !== 'empty' ? '—' : lawsuits.length
            }}</span>
            <span class="alg-kpi__sub" *ngIf="!sectionState(sections.lawsuits); else lawsuitsShort"
              ><strong [class.is-bad]="lawsuitCounts.defendant > 0">{{ lawsuitCounts.defendant }} como demandado</strong> ·
              {{ lawsuitCounts.plaintiff }} como demandante<ng-container *ngIf="lawsuitCounts.unconfirmed">
                · {{ lawsuitCounts.unconfirmed }} por confirmar</ng-container
              ></span
            >
            <ng-template #lawsuitsShort
              ><span class="alg-kpi__sub">{{ stateShort[sectionState(sections.lawsuits)!] }}</span></ng-template
            >
          </div>
          <!-- BIL no documenta su escala de grado: se dice qué es el número, no qué significa. -->
          <div class="alg-kpi" data-test="bil-max-grade">
            <span class="alg-kpi__label" title="Grado máximo de riesgo que reporta BIL">BIL · grado máximo</span>
            <span class="alg-kpi__value" data-test="bil-max-grade-value">{{ sections.bil?.data?.maxGrade ?? '—' }}</span>
            <span class="alg-kpi__sub" *ngIf="sections.bil?.data as bil; else bilShort"
              >{{ bil.hits.length }} {{ bil.hits.length === 1 ? 'coincidencia' : 'coincidencias' }} · {{ shortDate(bil.date) }}</span
            >
            <ng-template #bilShort
              ><span class="alg-kpi__sub">{{ stateShort[sectionState(sections.bil)!] }}</span></ng-template
            >
          </div>
          <div class="alg-kpi" data-test="shareholders-kpi">
            <span class="alg-kpi__label">Accionistas</span>
            <span class="alg-kpi__value">{{ sections.shareholders?.data?.items?.length ?? '—' }}</span>
            <span class="alg-kpi__sub" *ngIf="shareholderSummary as summary; else shareholdersShort"
              >fuente {{ summary.sources
              }}<ng-container *ngIf="summary.withoutPercentage"> · {{ summary.withoutPercentage }} sin porcentaje</ng-container></span
            >
            <ng-template #shareholdersShort
              ><span class="alg-kpi__sub">{{ stateShort[sectionState(sections.shareholders)!] }}</span></ng-template
            >
          </div>
        </div>

        <div class="co-defendant" *ngIf="coDefendants.length" role="note" data-test="co-defendant">
          <i class="fa-solid fa-triangle-exclamation" aria-hidden="true"></i>
          <span><strong>Socio o administrador codemandado:</strong> {{ coDefendants.join(', ') }}</span>
        </div>

        <!-- ─── Expedientes judiciales ─── -->
        <div class="alg-block" data-test="lawsuits">
          <div class="alg-sec">
            <h3>Expedientes judiciales</h3>
            <div class="alg-chips lawsuit-chips" *ngIf="lawsuits.length" role="group" aria-label="Filtrar expedientes por papel">
              <button
                *ngFor="let filter of lawsuitFilters; trackBy: trackByIndex"
                type="button"
                class="alg-chip"
                [attr.data-test]="'lawsuit-filter-' + filter.key"
                [class.is-on]="lawsuitFilter === filter.key"
                [attr.aria-pressed]="lawsuitFilter === filter.key"
                (click)="setLawsuitFilter(filter.key)"
              >
                {{ filter.label
                }}<span class="alg-chip__n"><span class="p-hidden-accessible">: </span>{{ lawsuitCounts[filter.key] }}</span>
              </button>
            </div>
            <span class="alg-aside" *ngIf="lawsuits.length"
              >El papel lo deduce el sistema; la «Actuación» de Syntage es solo referencia</span
            >
          </div>

          <app-snapshot-state
            *ngIf="sectionState(sections.lawsuits); else lawsuitTable"
            [state]="sectionState(sections.lawsuits)!"
            [closesAt]="snapshot.windowEndsAt"
            [reason]="sections.lawsuits?.reason"
          ></app-snapshot-state>
          <ng-template #lawsuitTable>
            <div class="alg-table-wrap">
              <table class="alg-table lawsuits-table">
                <caption class="p-hidden-accessible">
                  Expedientes judiciales con el papel del cliente
                </caption>
                <colgroup>
                  <col style="width: 44px" />
                  <col style="width: 110px" />
                  <col style="width: 120px" />
                  <col style="width: 150px" />
                  <col class="is-fill-16" />
                  <col style="width: 150px" />
                  <col style="width: 110px" />
                  <col class="is-fill-10" />
                  <col style="width: 60px" />
                </colgroup>
                <thead>
                  <tr>
                    <th scope="col"><span class="p-hidden-accessible">Detalle</span></th>
                    <th scope="col">Expediente</th>
                    <th scope="col">Papel</th>
                    <th scope="col">Materia</th>
                    <th scope="col">Juzgado</th>
                    <th scope="col">Ciudad</th>
                    <th scope="col">1er acuerdo</th>
                    <th scope="col">Contraparte</th>
                    <th scope="col" class="is-num">Notif.</th>
                  </tr>
                </thead>
                <tbody>
                  <ng-container *ngFor="let lawsuit of filteredLawsuits; trackBy: trackByIndex">
                    <tr data-test="lawsuit" [class.is-open]="openRows.has(lawsuitKey(lawsuit))">
                      <td>
                        <button
                          type="button"
                          class="alg-exp"
                          data-test="lawsuit-toggle"
                          [attr.aria-expanded]="openRows.has(lawsuitKey(lawsuit))"
                          [attr.aria-label]="'Ver el detalle del expediente ' + (lawsuit.caseNumber ?? '')"
                          (click)="toggleRow(lawsuitKey(lawsuit))"
                        >
                          <i class="fa-solid fa-chevron-right" aria-hidden="true"></i>
                        </button>
                      </td>
                      <td class="is-mono">{{ lawsuit.caseNumber ?? '—' }}</td>
                      <td>
                        <span class="alg-pill" data-test="lawsuit-role" [ngClass]="'is-' + roleTone(lawsuit)">{{
                          roleOf(lawsuit).label
                        }}</span>
                      </td>
                      <td>
                        {{ lawsuit.matter ?? '—' }}<ng-container *ngIf="lawsuit.caseType"> · {{ lawsuit.caseType }}</ng-container>
                      </td>
                      <td class="lawsuit-court">{{ lawsuit.court ?? '—' }}</td>
                      <td>
                        {{ lawsuit.city ?? '—' }}<ng-container *ngIf="lawsuit.state">, {{ lawsuit.state }}</ng-container>
                      </td>
                      <td class="is-num-plain">
                        {{ shortDate(lawsuit.firstAgreementDate) }}
                        <!-- `publishedAt` llega como texto del proveedor (DD/MM/AAAA): se muestra tal cual para no invertir día y mes. -->
                        <span class="alg-table__sub" *ngIf="lawsuit.publishedAt">publicado {{ lawsuit.publishedAt }}</span>
                      </td>
                      <td>{{ counterpartOf(lawsuit) }}</td>
                      <td class="is-num">{{ lawsuit.notifications.length }}</td>
                    </tr>
                    <tr class="alg-detail" *ngIf="openRows.has(lawsuitKey(lawsuit))" data-test="lawsuit-detail">
                      <td colspan="9">
                        <div class="alg-detail__grid lawsuit-detail">
                          <div>
                            <div class="alg-lbl">Actor</div>
                            <div class="alg-detail__value">{{ lawsuit.plaintiff ?? '—' }}</div>
                          </div>
                          <div>
                            <div class="alg-lbl">Demandado</div>
                            <div class="alg-detail__value">{{ lawsuit.defendant ?? '—' }}</div>
                          </div>
                          <div>
                            <div class="alg-lbl">Actuación · dato del proveedor</div>
                            <div class="alg-detail__value" data-test="provider-action">{{ lawsuit.providerAction ?? '—' }}</div>
                            <div class="detail-note" data-test="lawsuit-confidence">{{ confidenceLabel(lawsuit) }}</div>
                          </div>
                          <div data-test="notifications">
                            <div class="alg-lbl">Notificaciones</div>
                            <div class="notification" *ngFor="let notification of lawsuit.notifications; trackBy: trackByIndex">
                              <strong>{{ shortDate(notification.date) }}</strong
                              ><ng-container *ngIf="notification.type"> · {{ notification.type }}</ng-container> ·
                              {{ notification.text ?? '—' }}
                            </div>
                            <div class="alg-detail__value" *ngIf="!lawsuit.notifications.length">—</div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  </ng-container>
                  <tr *ngIf="!filteredLawsuits.length">
                    <td colspan="9" class="alg-table__empty" data-test="lawsuits-none">Ningún expediente con ese papel.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </ng-template>
        </div>

        <div class="alg-grid legal-grid">
          <!-- ─── Antecedentes por categoría ─── -->
          <div class="alg-block" data-test="background">
            <div class="alg-sec">
              <h3>Antecedentes por categoría</h3>
              <span class="alg-aside" data-test="background-scale">rojo solo si Syntage baja la calificación</span>
            </div>
            <app-snapshot-state
              *ngIf="sectionState(sections.background); else backgroundTable"
              [state]="sectionState(sections.background)!"
              [closesAt]="snapshot.windowEndsAt"
              [reason]="sections.background?.reason"
            ></app-snapshot-state>
            <ng-template #backgroundTable>
              <div class="alg-table-wrap">
                <table class="alg-table background-table">
                  <caption class="p-hidden-accessible">
                    Antecedentes por categoría, registros y bases consultadas
                  </caption>
                  <colgroup>
                    <col />
                    <col style="width: 190px" />
                    <col style="width: 70px" />
                    <col style="width: 56px" />
                    <col style="width: 110px" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th scope="col">Categoría</th>
                      <th scope="col">Estado</th>
                      <th scope="col" class="is-num">Calif.</th>
                      <th scope="col" class="is-num"><abbr title="Registros">Reg.</abbr></th>
                      <th scope="col" class="is-num"><abbr title="Bases consultadas de las que tiene la categoría">Bases</abbr></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr *ngFor="let row of backgroundRows; trackBy: trackByIndex" data-test="background-row">
                      <td>{{ row.label }}</td>
                      <td>
                        <span class="alg-pill background-pill" data-test="background-verdict" [ngClass]="'is-' + row.tone">{{
                          row.verdict.label
                        }}</span>
                      </td>
                      <td class="is-num" data-test="background-category-score">{{ row.score }}</td>
                      <td class="is-num" data-test="background-records">{{ row.records }}</td>
                      <td class="is-num is-secondary" data-test="background-databases">{{ row.databases }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </ng-template>
          </div>

          <!-- ─── Reporte BIL ─── -->
          <div class="alg-block" data-test="bil">
            <div class="alg-sec">
              <h3>Reporte BIL</h3>
              <span class="bil-meta" *ngIf="sections.bil?.data as bil"
                >folio <span class="alg-mono">{{ bil.folio ?? '—' }}</span> · {{ shortDate(bil.date) }}</span
              >
              <span class="bil-actions" *ngIf="hasBilPdf">
                <span class="alg-file-note" *ngIf="fileNote('bil') as note" data-test="pdf-bil-note">{{ note }}</span>
                <app-snapshot-pdf-button
                  *ngIf="offersFile('bil')"
                  data-test="pdf-bil"
                  subject="el reporte BIL"
                  [status]="fileStatus('bil')"
                  [opening]="downloading === 'bil'"
                  (open)="openFile('bil')"
                ></app-snapshot-pdf-button>
              </span>
            </div>
            <app-snapshot-state
              *ngIf="sectionState(sections.bil); else bilContent"
              [state]="sectionState(sections.bil)!"
              [closesAt]="snapshot.windowEndsAt"
              [reason]="sections.bil?.reason"
            ></app-snapshot-state>
            <p class="bil-hint" *ngIf="suggestsBilSearch" data-test="bil-search-hint">
              <i class="fa-solid fa-magnifying-glass" aria-hidden="true"></i>
              El reporte BIL se busca junto con el de Buró: búscalo desde la pestaña Buró y deuda, con «Buscar reporte en Syntage».
            </p>
            <ng-template #bilContent>
              <div class="alg-table-wrap">
                <table class="alg-table bil-table">
                  <caption class="p-hidden-accessible">
                    Coincidencias del reporte BIL
                  </caption>
                  <colgroup>
                    <col style="width: 44px" />
                    <col />
                    <col style="width: 80px" />
                    <col class="is-fill-bil" />
                    <col style="width: 60px" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th scope="col"><span class="p-hidden-accessible">Detalle</span></th>
                      <th scope="col">Fuente</th>
                      <th scope="col">Clave</th>
                      <th scope="col">Dato clave</th>
                      <th scope="col" class="is-num">Grado</th>
                    </tr>
                  </thead>
                  <tbody>
                    <ng-container *ngFor="let hit of bilHits; let i = index; trackBy: trackByIndex">
                      <tr data-test="bil-hit" [class.is-open]="openRows.has('bil:' + i)">
                        <td>
                          <button
                            type="button"
                            class="alg-exp"
                            data-test="bil-toggle"
                            [attr.aria-expanded]="openRows.has('bil:' + i)"
                            aria-label="Ver todos los campos de la coincidencia"
                            (click)="toggleRow('bil:' + i)"
                          >
                            <i class="fa-solid fa-chevron-right" aria-hidden="true"></i>
                          </button>
                        </td>
                        <td>
                          {{ hit.source }}
                          <span class="alg-table__sub">{{ hit.type }}</span>
                        </td>
                        <td class="is-num-plain">{{ hit.code }}</td>
                        <td class="is-num-plain" data-test="bil-key-data">{{ hit.keyData }}</td>
                        <td class="is-num">{{ hit.grade }}</td>
                      </tr>
                      <tr class="alg-detail" *ngIf="openRows.has('bil:' + i)" data-test="bil-hit-detail">
                        <td colspan="5">
                          <dl class="alg-detail__grid bil-fields">
                            <div *ngFor="let field of hit.fields; trackBy: trackByIndex" data-test="bil-hit-field">
                              <dt class="alg-lbl">{{ field.label }}</dt>
                              <dd>{{ field.value }}</dd>
                            </div>
                          </dl>
                        </td>
                      </tr>
                    </ng-container>
                  </tbody>
                </table>
              </div>
            </ng-template>
          </div>
        </div>

        <!-- ─── Corporativo: la sociedad y sus accionistas arriba, los actos inscritos abajo a todo el ancho ─── -->
        <div class="alg-divider"><span>Corporativo</span></div>

        <div class="corporate-top" [class.is-single]="!companies.length">
          <div class="alg-block" data-test="corporate" *ngIf="companies.length">
            <div class="alg-sec"><h3>Registro Público de Comercio</h3></div>
            <article class="alg-box company-card" *ngFor="let company of companies; trackBy: trackByIndex" data-test="company">
              <header class="company-head">
                <h4 class="company-name">{{ company.name ?? '—' }}</h4>
                <span class="company-type" *ngIf="company.legalRegime">{{ company.legalRegime }}</span>
              </header>
              <div class="company-meta">
                <span class="alg-pill is-neutral" *ngIf="company.fme" data-test="company-fme"
                  >FME <span class="alg-mono">{{ company.fme }}</span
                  ><ng-container *ngIf="company.fmeStatus"> · {{ company.fmeStatus }}</ng-container></span
                >
                <span class="company-fact" *ngIf="company.registeredAt"
                  ><span class="alg-lbl">Constitución</span> {{ shortDate(company.registeredAt) }}</span
                >
              </div>
              <dl class="company-fields">
                <div>
                  <dt class="alg-lbl">Objeto social</dt>
                  <dd>{{ company.corporatePurpose ?? '—' }}</dd>
                </div>
                <div>
                  <dt class="alg-lbl">Domicilio</dt>
                  <dd>
                    {{ company.address ?? '—' }}<ng-container *ngIf="company.state">, {{ company.state }}</ng-container>
                  </dd>
                </div>
              </dl>
            </article>
          </div>

          <div class="alg-block" data-test="shareholders">
            <div class="alg-sec">
              <h3>Accionistas</h3>
              <span class="alg-aside">fuentes: RPC antes que Buró</span>
            </div>
            <app-snapshot-state
              *ngIf="sectionState(sections.shareholders); else shareholdersTable"
              [state]="sectionState(sections.shareholders)!"
              [closesAt]="snapshot.windowEndsAt"
              [reason]="sections.shareholders?.reason"
            ></app-snapshot-state>
            <ng-template #shareholdersTable>
              <div class="alg-table-wrap">
                <table class="alg-table shareholders-table">
                  <caption class="p-hidden-accessible">
                    Accionistas con su participación y la fuente del dato
                  </caption>
                  <colgroup>
                    <col />
                    <col style="width: 110px" />
                    <col style="width: 90px" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th scope="col">Accionista</th>
                      <th scope="col" class="is-num">Participación</th>
                      <th scope="col">Fuente</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr *ngFor="let holder of sections.shareholders?.data?.items ?? []; trackBy: trackByIndex" data-test="shareholder-row">
                      <td>
                        {{ holder.name }}
                        <span class="alg-table__sub alg-mono" *ngIf="holder.rfc">{{ holder.rfc }}</span>
                      </td>
                      <td class="is-num">{{ holder.percentage !== null ? holder.percentage + ' %' : '—' }}</td>
                      <td>
                        <span class="alg-pill is-neutral">{{ sourceLabel(holder.source) }}</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div class="shareholders-note" *ngIf="shareholdersTotalNote" role="note" data-test="shareholders-total">
                  {{ shareholdersTotalNote }}
                </div>
              </div>
            </ng-template>
          </div>
        </div>

        <!-- Actos inscritos: tabla densa con detalle expandible (maqueta Legal.dc.html, como expedientes y BIL). -->
        <div class="alg-block" data-test="acts" *ngIf="acts.length">
          <div class="alg-sec">
            <h3>Actos inscritos</h3>
            <span class="alg-pill is-neutral">{{ acts.length }}</span>
          </div>
          <div class="alg-table-wrap">
            <table class="alg-table acts-table">
              <caption class="p-hidden-accessible">
                Actos inscritos en el Registro Público de Comercio
              </caption>
              <colgroup>
                <col style="width: 44px" />
                <col />
                <col style="width: 120px" />
                <col style="width: 200px" />
                <col style="width: 100px" />
              </colgroup>
              <thead>
                <tr>
                  <th scope="col"><span class="p-hidden-accessible">Detalle</span></th>
                  <th scope="col">Acto</th>
                  <th scope="col">Inscripción</th>
                  <th scope="col">Documento</th>
                  <th scope="col" class="is-num">Personas</th>
                </tr>
              </thead>
              <tbody>
                <ng-container *ngFor="let act of acts; let a = index; trackBy: trackByIndex">
                  <tr data-test="act-row" [class.is-open]="openRows.has('act:' + a)">
                    <td>
                      <button
                        *ngIf="act.people?.length"
                        type="button"
                        class="alg-exp"
                        data-test="act-toggle"
                        [attr.aria-expanded]="openRows.has('act:' + a)"
                        [attr.aria-label]="'Ver las personas del acto ' + (act.act ?? '')"
                        (click)="toggleRow('act:' + a)"
                      >
                        <i class="fa-solid fa-chevron-right" aria-hidden="true"></i>
                      </button>
                    </td>
                    <td>
                      <span class="act-name">{{ act.act ?? '—' }}</span>
                      <span class="alg-table__sub" *ngIf="act.form">{{ act.form }}</span>
                    </td>
                    <td class="is-num-plain">{{ shortDate(act.registeredAt) }}</td>
                    <td>
                      <span class="alg-mono">{{ act.documentNumber ?? '—' }}</span>
                      <!-- El reductor lee las personas una sola vez por documento: las de este acto son las del de arriba. -->
                      <span class="alg-table__sub" *ngIf="sharesPreviousDocument(a)" data-test="act-same-document"
                        >mismo documento que el acto anterior</span
                      >
                    </td>
                    <td class="is-num">
                      <span class="alg-pill is-neutral" *ngIf="act.people?.length; else noPeople" data-test="act-people-count">{{
                        act.people?.length
                      }}</span>
                      <ng-template #noPeople
                        ><span
                          data-test="act-no-people"
                          [attr.title]="
                            sharesPreviousDocument(a) ? 'Mismo documento que el acto anterior' : 'Sin personas registradas en este acto.'
                          "
                          >—<span class="p-hidden-accessible"
                            >:
                            {{
                              sharesPreviousDocument(a) ? 'Mismo documento que el acto anterior' : 'Sin personas registradas en este acto.'
                            }}</span
                          ></span
                        ></ng-template
                      >
                    </td>
                  </tr>
                  <tr class="alg-detail" *ngIf="act.people?.length && openRows.has('act:' + a)" data-test="act-detail">
                    <td colspan="5">
                      <table class="alg-table people-table">
                        <caption class="p-hidden-accessible">
                          {{
                            'Personas del acto ' + (act.act ?? '')
                          }}
                        </caption>
                        <colgroup>
                          <col class="is-fill-person" />
                          <col style="width: 170px" />
                          <col style="width: 110px" />
                          <col style="width: 150px" />
                          <col class="is-fill-power" />
                        </colgroup>
                        <thead>
                          <tr>
                            <th scope="col">Nombre</th>
                            <th scope="col">Cargo</th>
                            <th scope="col">Estado</th>
                            <th scope="col">Acciones / capital</th>
                            <th scope="col">Poder</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr *ngFor="let person of act.people ?? []; let p = index; trackBy: trackByIndex" data-test="act-person">
                            <td class="person-name">{{ person.name }}</td>
                            <td>{{ person.position ?? '—' }}</td>
                            <td>
                              <span class="alg-pill is-neutral" *ngIf="person.change; else noChange" data-test="person-change">{{
                                person.change
                              }}</span>
                              <ng-template #noChange>—</ng-template>
                            </td>
                            <td class="is-num-plain person-role">
                              <ng-container *ngIf="person.shares !== undefined || person.total !== undefined; else noShares"
                                >{{ person.shares ?? '—' }} {{ person.shares === 1 ? 'acción' : 'acciones'
                                }}<ng-container *ngIf="person.total !== undefined"> · {{ money(person.total) }}</ng-container></ng-container
                              >
                              <ng-template #noShares>—</ng-template>
                            </td>
                            <td>
                              <ng-container *ngIf="person.powersSummary; else noPowers">
                                <span
                                  class="person-powers"
                                  [attr.title]="person.powersSummary"
                                  [class.is-open]="openRows.has('power:' + a + ':' + p)"
                                  [attr.id]="'act-' + a + '-person-' + p + '-powers'"
                                  data-test="person-powers"
                                  >{{ person.powersSummary }}</span
                                >
                                <!-- Siempre que haya poder: en una línea recortada no se sabe cuánto cabe, así que nunca queda sin forma de abrirse. -->
                                <button
                                  type="button"
                                  class="alg-link person-more"
                                  data-test="person-more"
                                  [attr.aria-expanded]="openRows.has('power:' + a + ':' + p)"
                                  [attr.aria-controls]="'act-' + a + '-person-' + p + '-powers'"
                                  (click)="toggleRow('power:' + a + ':' + p)"
                                >
                                  {{ openRows.has('power:' + a + ':' + p) ? 'Ver menos' : 'Ver más' }}
                                </button>
                              </ng-container>
                              <ng-template #noPowers>—</ng-template>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </td>
                  </tr>
                </ng-container>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Poderes con registros: sin forma fija; una tarjeta por registro con sus campos, como las personas de los actos -->
        <div class="alg-block" data-test="powers" *ngIf="!sectionState(sections.powers)">
          <div class="alg-sec"><h3>Poderes vigentes</h3></div>
          <div class="people-grid">
            <dl class="person record-fields" *ngFor="let power of powerRecords; trackBy: trackByIndex" data-test="power-record">
              <div *ngFor="let field of entries(power); trackBy: trackByIndex">
                <dt class="alg-lbl">{{ humanize(field.key) }}</dt>
                <dd>{{ field.value }}</dd>
              </div>
            </dl>
          </div>
        </div>

        <div class="alg-none" *ngIf="corporateWithoutRecords.length" data-test="corporate-without-records">
          <span class="alg-none__label">Sin registros:</span>
          <ng-container *ngFor="let source of corporateWithoutRecords; trackBy: trackByIndex">
            <ng-container *ngTemplateOutlet="sourceChip; context: { $implicit: source }"></ng-container>
          </ng-container>
        </div>
      </div>
    </article>
  </ng-template>
</section>
